import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { extractTenantIdFromPath } from './tenant-id.util';

export interface TenantBranding {
  logo?: string;
  mainColor?: string;
  secondaryColor?: string;
  /** When {@code true}, the login page renders only the SSO button. */
  useSso?: boolean;
}

/**
 * Centralises all tenant branding logic:
 *   1. Before login  → loadByTenantId() reads the tenant id from the URL path
 *                       (e.g. /app/{id}/...) and calls the public
 *                       /tenants/public/branding endpoint.
 *   2. After login   → applyBranding() is called with the TenantBranding from
 *                       /tenants/current (passed in by AppComponent / AuthService).
 *
 * Both paths call applyToRoot() which writes --primary-color / --secondary-color
 * on :root and stores the resolved logo / SSO flag for components that need it.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  /** The tenant id currently resolved from the URL (null = generic / no tenant). */
  currentTenantId: string | null = null;

  private readonly defaults = environment.colors;
  private readonly brandingSubject = new BehaviorSubject<TenantBranding>({});
  /**
   * {@code true} once {@link #loadByTenantId()} has finished (success or failure), or
   * immediately when the URL has no {@code /app/{id}} segment.
   */
  private readonly tenantBrandingResolvedSubject = new BehaviorSubject<boolean>(
    extractTenantIdFromPath(window.location.pathname) == null
  );

  readonly branding$: Observable<TenantBranding> = this.brandingSubject.asObservable();
  readonly tenantBrandingResolved$: Observable<boolean> = this.tenantBrandingResolvedSubject.asObservable();

  /** Stream of the currently applied tenant logo URL. Derived from {@link #branding$}. */
  readonly currentLogo$: Observable<string | null> = this.branding$.pipe(
    map(b => b.logo ?? null)
  );

  constructor(private http: HttpClient) {}

  /**
   * Resolves the tenant id from the current URL path and, if one exists,
   * fetches the matching tenant branding from the public endpoint.
   * Safe to call unconditionally on app init — does nothing when no id
   * is present (generic login, neutral colours).
   */
  loadByTenantId(): Observable<TenantBranding> {
    const pathTenantId = extractTenantIdFromPath(window.location.pathname);
    const fallbackTenantId = (environment.defaultTenantId || '').trim() || null;
    const id = pathTenantId ?? fallbackTenantId;
    this.currentTenantId = id;
    if (!id) {
      this.tenantBrandingResolvedSubject.next(true);
      return EMPTY;
    }
    const url = `${environment.tenantsUrl}/public/branding?id=${encodeURIComponent(id)}`;
    return this.http.get<TenantBranding>(url).pipe(
      tap(branding => this.applyToRoot(branding ?? {})),
      catchError(() => {
        this.applyToRoot({});
        return of({});
      }),
      finalize(() => this.tenantBrandingResolvedSubject.next(true))
    );
  }

  /**
   * Applies a branding object to the DOM. Called after login with the data from
   * /tenants/current. Pass null / undefined to reset to environment defaults.
   */
  applyBranding(branding: TenantBranding | null | undefined): void {
    this.applyToRoot(branding ?? {});
  }

  // ── private ────────────────────────────────────────────────────────────────

  private applyToRoot(branding: TenantBranding): void {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', branding.mainColor || this.defaults.primary);
    root.style.setProperty('--secondary-color', branding.secondaryColor || this.defaults.secondary);
    this.brandingSubject.next(branding);
  }

}
